import { MessageCircle, MessageCircleQuestion } from 'lucide-react'
import type { ReactNode } from 'react'
import appWindow from '@/assets/app-window.svg'
import squareDashedMousePointer from '@/assets/square-dashed-mouse-pointer.svg'
import { FEATURE_FLAGS } from '@/constants/featureFlags'
import { useOrganizationFeatureFlag } from '@/hooks/useOrganizationFeatureFlag'

export type DeploymentType = 'embed' | 'full_screen' | 'pdp_qa' | 'widget'

interface DeploymentOption {
  type: DeploymentType
  label: string
  description: string
  renderIcon: () => ReactNode
}

// Named icon renderers rather than inline arrows in the catalog: the first two
// are Figma-exported SVGs shown via <img> (brightness/invert for dark mode),
// the third is a Lucide component — a single `icon: X` field can't cover both.
function onPageIcon(): ReactNode {
  return (
    <img
      src={squareDashedMousePointer}
      alt=""
      className="w-4 h-4 brightness-0 dark:brightness-100 dark:invert"
    />
  )
}

function ownedWebIcon(): ReactNode {
  return (
    <img
      src={appWindow}
      alt=""
      className="w-4 h-4 brightness-0 dark:brightness-100 dark:invert"
    />
  )
}

function productQaIcon(): ReactNode {
  return <MessageCircleQuestion className="w-4 h-4 text-foreground" />
}

function widgetIcon(): ReactNode {
  return <MessageCircle className="w-4 h-4 text-foreground" />
}

// Single source of truth for the deployment choices shown at agent creation,
// shared by both CreateAgentModal variants so the card set can't drift.
export const DEPLOYMENT_OPTIONS: readonly DeploymentOption[] = [
  {
    type: 'embed',
    label: 'On-page',
    description: 'Embedded into your existing site',
    renderIcon: onPageIcon,
  },
  {
    type: 'full_screen',
    label: 'Owned web',
    description: 'Standalone page for full control',
    renderIcon: ownedWebIcon,
  },
  {
    type: 'widget',
    label: 'Sitewide widget',
    description: 'Corner launcher across your existing site',
    renderIcon: widgetIcon,
  },
  {
    type: 'pdp_qa',
    label: 'Product Q&A',
    description: 'Single-turn answers on your product pages',
    renderIcon: productQaIcon,
  },
]

interface DeploymentTypeCardsProps {
  selected: DeploymentType | null
  onSelect: (type: DeploymentType) => void
}

/**
 * The deployment-type card picker, rendered from DEPLOYMENT_OPTIONS.
 *
 * The Product Q&A (pdp_qa) card is gated behind the PDP_QA org feature flag —
 * the whole surface ships dark until an org is opted in. While the flag query
 * is in flight we hide it too, to avoid a card flashing in then out.
 */
export function DeploymentTypeCards({
  selected,
  onSelect,
}: DeploymentTypeCardsProps) {
  const { enabled: pdpQaEnabled, isLoading: pdpQaFlagLoading } =
    useOrganizationFeatureFlag(FEATURE_FLAGS.PDP_QA)

  const showPdpQa = pdpQaEnabled && !pdpQaFlagLoading
  const { enabled: widgetEnabled, isLoading: widgetFlagLoading } =
    useOrganizationFeatureFlag(FEATURE_FLAGS.ONSITE_WIDGET)
  const showWidget = widgetEnabled && !widgetFlagLoading

  const options = DEPLOYMENT_OPTIONS.filter(
    (option) =>
      (option.type !== 'pdp_qa' || showPdpQa) &&
      (option.type !== 'widget' || showWidget)
  )

  return (
    <div className="flex flex-wrap gap-3">
      {options.map((option) => {
        const isSelected = selected === option.type

        return (
          <div
            key={option.type}
            role="button"
            tabIndex={0}
            className={`flex-1 min-w-[9rem] border rounded-2xl p-3 cursor-pointer transition-all ${
              isSelected
                ? 'border-ring ring-[3px] ring-ring/50'
                : 'border-border hover:border-border/80'
            }`}
            onClick={() => onSelect(option.type)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect(option.type)
              }
            }}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {option.renderIcon()}
              <span className="text-sm font-medium text-foreground">
                {option.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {option.description}
            </p>
          </div>
        )
      })}
    </div>
  )
}
